// Manager-portal page for the agency-wide settings that every agent
// proptree inherits. Stored alongside the agency-default doc under
// `agents.*` (mirrors legacy enterprise-app's "Agent links" tab —
// /Users/martinploug/Desktop/Adbooster/enterprise-app/src/components/createNewProptree.js).
// Editable surfaces (per the design discussion):
//   1. Agency-controlled buttons — rendered AFTER the agent's own
//      buttons on every agent page.
//   2. Top section banner — separate slogan list from the agency page.
//   3. What listings to show — all enterprise listings vs only the
//      agent's own (legacy `showAllAgencyListings`).
//   4. Partner buttons — separate toggle map from the agency page.
//   5. Agents marquee — the cross-agents strip on agent pages.
//   6. Card banner — between-listings agency promo card.
//   7. Newsletter — "Stay in the loop" signup card switch + copy.
//
// Save endpoint isn't wired yet (mirrors the agency-page TODO); the
// manager keeps the form state local and only the preview iframe sees
// edits via postMessage.
import * as React from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronDown,
  ExternalLink,
  Home,
  Mail,
  Megaphone,
  MousePointerClick,
  Plus,
  Trash2,
  Undo2,
} from 'lucide-react';
import type {
  AgentsProptreePickerItem,
  AgentsProptreeResponse,
  AgentsProptreeSettings,
  ProfileButton,
  ProptreeEnumValue,
} from '@proptr/shared';
import {
  NEWSLETTER_DEFAULT_SUBTITLE,
  NEWSLETTER_DEFAULT_TITLE,
} from '@proptr/shared';
import { ApiError } from '@proptr/api-client';
import { AddProptreeButtonModal } from '../components/AddProptreeButtonModal.js';
import { NewsletterCopyFields } from '../components/NewsletterCopyFields.js';
import { ProptreeButtonRow } from '../components/ProptreeButtonRow.js';
import { SectionCard } from '../components/SectionCard.js';
import { PartnerToggleList } from '../components/PartnerToggleList.js';
import { AppShell, useBannerSlot } from '../components/layout/AppShell.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import { Select, type SelectOption } from '../components/ui/select.js';
import { withDemoFlag } from '../lib/testMode.js';
import { cn, initials } from '../lib/utils.js';
import { client } from '../main.js';

const PUBLIC_URL =
  (import.meta.env.VITE_PUBLIC_URL as string | undefined) ??
  'http://localhost:4040';

// Same enum lists used on the agency page — kept lean by inlining
// rather than reaching into the agency module so this file is
// self-contained.
const AGENCY_BANNER_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'Disabled', value: 'disabled' },
  { id: 2, name: 'Enabled', value: 'enabled' },
];
const BANNER_STATS_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'Show stats', value: 'enabled' },
  { id: 2, name: 'Hide stats', value: 'disabled' },
];
const AGENT_FEED_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: 'Enabled', value: 'enabled' },
  { id: 2, name: 'Disabled', value: 'disabled' },
];
const SHOW_ALL_LISTINGS_OPTIONS: ProptreeEnumValue[] = [
  { id: 1, name: "Only the agent's own listings", value: 'disabled' },
  { id: 2, name: 'All agency listings', value: 'enabled' },
];

// === Slogan limits, mirroring the agency page. =======================
const SLOGAN_CHAR_LIMIT = 60;
const MAX_CUSTOM_SLOGANS = 10;

interface BannerSlogans {
  enabled: boolean;
  presets: {
    activeListings: boolean;
    agents: boolean;
    listingsForSale: boolean;
    listingsForRent: boolean;
  };
  customs: string[];
}

// === Form state =====================================================
interface FormState {
  showAgencyBanner: ProptreeEnumValue | null;
  agencyBannerStats: ProptreeEnumValue | null;
  showAgentFeed: ProptreeEnumValue | null;
  showAllAgencyListings: ProptreeEnumValue | null;
  agencyButtons: ProfileButton[];
  partners: Record<string, boolean>;
  bannerSlogans: BannerSlogans;
  // "Stay in the loop" card on agent pages. Copy is prefilled with the
  // defaults; blank saves as null → the public card shows the defaults.
  newsletter: boolean;
  newsletterTitle: string;
  newsletterSubtitle: string;
}

function initialFormState(s: AgentsProptreeSettings): FormState {
  return {
    showAgencyBanner: s.showAgencyBanner,
    agencyBannerStats: s.agencyBannerStats,
    showAgentFeed: s.showAgentFeed,
    showAllAgencyListings: s.showAllAgencyListings,
    agencyButtons: s.agencyButtons,
    partners: s.partners,
    bannerSlogans: s.bannerSlogans,
    newsletter: s.newsletter,
    newsletterTitle: s.newsletterTitle ?? NEWSLETTER_DEFAULT_TITLE,
    newsletterSubtitle: s.newsletterSubtitle ?? NEWSLETTER_DEFAULT_SUBTITLE,
  };
}

// === Page shell ====================================================
export function AgentsProptreeSettingsPage() {
  const [data, setData] = React.useState<AgentsProptreeResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [notFound, setNotFound] = React.useState(false);
  // Same list the agency page loads — agency-controlled buttons of type
  // "form" pick from it. Without this the picker was always empty.
  const [formOptions, setFormOptions] = React.useState<ProptreeEnumValue[]>([]);

  React.useEffect(() => {
    let alive = true;
    setError(null);
    setNotFound(false);
    client.forms
      .list({ status: 'active', limit: 100, sort: 'name', order: 'asc' })
      .then((res) => {
        if (!alive) return;
        setFormOptions(
          res.items.map((f, i) => ({
            id: i + 1,
            name: f.name ?? '(unnamed)',
            value: f._id,
          })),
        );
      })
      .catch(() => {
        // Non-fatal — the picker just stays empty.
      });
    client.proptrees
      .getAgentsProptree()
      .then((res) => {
        if (alive) setData(res);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
        } else {
          setError((err as Error).message);
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <AppShell>
      <div className="space-y-6 pb-10">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0">
            <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
              Agent proptrees
            </h1>
            <div className="mt-3 flex items-center gap-3">
              <Link
                to="/proptree"
                className="inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground transition-colors hover:text-foreground"
              >
                <ArrowLeft className="h-3 w-3" />
                Back to proptree
              </Link>
              <span className="text-muted-foreground/50">·</span>
              <div className="eyebrow">Proptree / Agents</div>
            </div>
          </div>
        </div>

        {error && (
          <Card className="border-destructive/40 bg-destructive/10 p-4 font-mono text-[11px] text-destructive">
            {error}
          </Card>
        )}

        {notFound && (
          <Card className="p-6">
            <p className="font-sans text-[13px] text-muted-foreground">
              No agency-default proptree exists for this organisation yet.
              Create the agency proptree first, then come back here to
              configure shared agent settings.
            </p>
          </Card>
        )}

        {!data && !error && !notFound && (
          <Card className="p-6 font-mono text-[11px] text-muted-foreground">
            Loading…
          </Card>
        )}

        {data && <SplitView data={data} formOptions={formOptions} />}
      </div>
    </AppShell>
  );
}

// === Split view: form on the left, preview on the right ============
function SplitView({
  data,
  formOptions,
}: {
  data: AgentsProptreeResponse;
  formOptions: ProptreeEnumValue[];
}) {
  const [form, setForm] = React.useState<FormState>(() =>
    initialFormState(data.settings),
  );
  const [original, setOriginal] = React.useState<FormState>(() =>
    initialFormState(data.settings),
  );
  // Undo stack mirrors the agency page — push the pre-edit form, pop
  // on undo. Cleared on save.
  const [history, setHistory] = React.useState<FormState[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    if (JSON.stringify(form[key]) === JSON.stringify(value)) return;
    setHistory((h) => [...h, form]);
    setForm({ ...form, [key]: value });
  };

  const handleUndo = () => {
    const restored = history[history.length - 1];
    // Index access is `T | undefined` under noUncheckedIndexedAccess; the
    // length check alone doesn't narrow it.
    if (!restored) return;
    setForm(restored);
    setHistory(history.slice(0, -1));
  };
  const canUndo = history.length > 0;

  const handleSave = async () => {
    setSaveError(null);
    setSaving(true);
    try {
      // Send only fields that actually changed so the API can leave
      // sibling keys it doesn't know about untouched.
      const patch: Record<string, unknown> = {};
      (Object.keys(form) as (keyof FormState)[]).forEach((k) => {
        if (JSON.stringify(form[k]) !== JSON.stringify(original[k])) {
          patch[k] = form[k];
        }
      });
      if (Object.keys(patch).length > 0) {
        await client.proptrees.updateAgentsProptree(
          patch as Parameters<typeof client.proptrees.updateAgentsProptree>[0],
        );
      }
      setOriginal(form);
      setHistory([]);
    } catch (err) {
      setSaveError(
        err instanceof ApiError ? err.message : (err as Error).message,
      );
    } finally {
      setSaving(false);
    }
  };

  const dirty = JSON.stringify(form) !== JSON.stringify(original);

  // Picker state — defaults to the first agent in the list (or null
  // when the agency has no agents yet, in which case the preview shows
  // a friendly placeholder).
  const [pickedId, setPickedId] = React.useState<string | null>(
    data.agents[0]?.id ?? null,
  );
  const pickedAgent =
    data.agents.find((a) => a.id === pickedId) ?? data.agents[0] ?? null;

  return (
    <div className="space-y-4">
      {dirty && (
        <UnsavedChangesBanner
          onSave={handleSave}
          onUndo={handleUndo}
          canUndo={canUndo}
          saving={saving}
          error={saveError}
        />
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <SettingsForm
          form={form}
          set={set}
          proptreeId={data._id}
          formOptions={formOptions}
        />
        <div className="lg:sticky lg:top-[calc(var(--header-h,4rem)+var(--banner-h,0px)+0.5rem)] lg:h-[calc(100dvh-var(--header-h,4rem)-var(--banner-h,0px)-1.5rem)]">
          <ProptreePreview
            form={form}
            agents={data.agents}
            pickedId={pickedAgent?.id ?? null}
            onPick={setPickedId}
          />
        </div>
      </div>
    </div>
  );
}

// === Top-of-page sticky save/undo banner ===========================
function UnsavedChangesBanner({
  onSave,
  onUndo,
  canUndo,
  saving = false,
  error = null,
}: {
  onSave: () => void;
  onUndo: () => void;
  canUndo: boolean;
  saving?: boolean;
  error?: string | null;
}) {
  const slot = useBannerSlot();
  if (!slot) return null;
  return createPortal(
    <div className="border-b border-primary/40 bg-primary/10 text-foreground dark:bg-primary/20">
      <div className="mx-auto flex w-full max-w-[1400px] items-center gap-3 px-6 py-2 text-[13px]">
        <span className="flex-1">
          {error
            ? `Save failed: ${error}`
            : 'You have updated agent proptree settings. Remember to save to reflect them on every agent page.'}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onUndo}
          disabled={!canUndo || saving}
          title="Undo last change"
        >
          <Undo2 />
          Undo
        </Button>
        <Button type="button" size="sm" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>,
    slot,
  );
}

// === Settings form =================================================
function SettingsForm({
  form,
  set,
  proptreeId,
  formOptions,
}: {
  form: FormState;
  set: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  /** Active forms, for buttons of type "form". */
  formOptions: ProptreeEnumValue[];
  // Owning agency-default proptree id — threaded down so image
  // uploads carry the right doc id in their Spaces metadata.
  proptreeId: string;
}) {
  const mainFeedEnabled =
    form.showAllAgencyListings == null
      ? true
      : form.showAllAgencyListings.value !== 'disabled-feed';

  return (
    <div className="space-y-4">
      <SectionCard
        title="Buttons"
        subtitle="Agency-controlled buttons shown after each agent's own"
        icon={MousePointerClick}
        defaultOpen
      >
      {/* Buttons — agency-controlled, rendered AFTER the agent's own. */}
      <ButtonsSection
        buttons={form.agencyButtons}
        onChange={(next) => set('agencyButtons', next)}
        partners={form.partners}
        onPartnersChange={(next) => set('partners', next)}
        proptreeId={proptreeId}
        formOptions={formOptions}
      />
      </SectionCard>

      <SectionCard
        title="Listings"
        subtitle="Listings filter and bottom agents marquee"
        icon={Home}
      >
      {/* Listings filter + agents marquee toggle */}
      <div className="p-5">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 sm:col-span-6">
            <Label>Listings to show</Label>
            <Select
              className="mt-1"
              options={SHOW_ALL_LISTINGS_OPTIONS.map<SelectOption>((o) => ({
                value: String(o.value),
                label: o.name ?? '',
              }))}
              value={String(form.showAllAgencyListings?.value ?? 'disabled')}
              onChange={(v) =>
                set(
                  'showAllAgencyListings',
                  SHOW_ALL_LISTINGS_OPTIONS.find((o) => o.value === v) ?? null,
                )
              }
            />
            <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
              Pick whether agent pages list every enterprise listing or
              just the listings that agent has been assigned. If the
              agent has no listings, the listings section is hidden.
            </p>
          </div>
          <div className="col-span-12 sm:col-span-6">
            <Label>Show agents</Label>
            <SwitchBox
              checked={form.showAgentFeed?.value === 'enabled'}
              onChange={(v) =>
                set(
                  'showAgentFeed',
                  AGENT_FEED_OPTIONS.find(
                    (o) => o.value === (v ? 'enabled' : 'disabled'),
                  ) ?? null,
                )
              }
              ariaLabel="Show agents"
            />
            <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
              Bottom marquee that lists every other agent on each agent
              page.
            </p>
          </div>
        </div>
      </div>

      </SectionCard>

      <SectionCard
        title="Banners"
        subtitle="Top marquee and agency banner card"
        icon={Megaphone}
      >
      {/* Marquee slogans + agency banner card — same tabbed UI as
          the agency page, but the values stored separately under
          `agents.*` so the agency tab can run a different message. */}
      <MarqueeSection
        value={form.bannerSlogans}
        onChange={(next) => set('bannerSlogans', next)}
        showAgencyBanner={form.showAgencyBanner}
        agencyBannerStats={form.agencyBannerStats}
        onShowAgencyBannerChange={(v) => set('showAgencyBanner', v)}
        onAgencyBannerStatsChange={(v) => set('agencyBannerStats', v)}
        showAgentFeed={form.showAgentFeed}
        mainFeedHidden={!mainFeedEnabled}
      />
      </SectionCard>

      <SectionCard
        title="Newsletter"
        subtitle="Stay in the loop email signup"
        icon={Mail}
      >
      {/* Stored under `agents.newsletter*`, separate from the agency
          page's card, so agent pages can run their own copy. */}
      <div className="p-5">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 sm:col-span-6">
            <Label>Show newsletter card</Label>
            <SwitchBox
              checked={form.newsletter}
              onChange={(v) => set('newsletter', v)}
              ariaLabel="Enable newsletter card"
            />
            <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
              Email-signup card shown above the footer of every agent
              page.
            </p>
          </div>
          {form.newsletter && (
            <NewsletterCopyFields
              idPrefix="agents-newsletter"
              title={form.newsletterTitle}
              subtitle={form.newsletterSubtitle}
              onTitleChange={(v) => set('newsletterTitle', v)}
              onSubtitleChange={(v) => set('newsletterSubtitle', v)}
            />
          )}
        </div>
      </div>
      </SectionCard>
    </div>
  );
}

// === Buttons section ==============================================
// Drag-reorder list of agency-controlled buttons + a Mortgage Finder
// partner toggle. Mirrors the agency page's `ButtonsSection` but
// without the Personal/Partner tabs — there's only one list here.
function ButtonsSection({
  buttons,
  onChange,
  partners,
  onPartnersChange,
  proptreeId,
  formOptions,
}: {
  buttons: ProfileButton[];
  onChange: (next: ProfileButton[]) => void;
  partners: Record<string, boolean>;
  onPartnersChange: (next: Record<string, boolean>) => void;
  /** Active forms, for buttons of type "form". */
  formOptions: ProptreeEnumValue[];
  // Owning agency-default proptree id — passed to row + modal so
  // button image uploads inherit the right proptree-id metadata on
  // Spaces.
  proptreeId: string;
}) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [draggingIndex, setDraggingIndex] = React.useState<number | null>(
    null,
  );
  const [tab, setTab] = React.useState<'agency' | 'partners'>('agency');

  const updateAt = (i: number, patch: Partial<ProfileButton>) =>
    onChange(buttons.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const removeAt = (i: number) =>
    onChange(buttons.filter((_, j) => j !== i));
  const handleDrop = (target: number) => {
    if (draggingIndex == null || draggingIndex === target) {
      setDraggingIndex(null);
      return;
    }
    const next = [...buttons];
    const [moved] = next.splice(draggingIndex, 1);
    // splice returns `T[]`, so the destructured element is `T | undefined`.
    if (moved) {
      next.splice(target, 0, moved);
      onChange(next);
    }
    setDraggingIndex(null);
  };

  return (
    <div className="p-5">
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <div className="inline-flex rounded-md border border-border bg-muted p-0.5 dark:border-white/10">
            <SectionTabButton
              active={tab === 'agency'}
              onClick={() => setTab('agency')}
            >
              Agency-controlled
            </SectionTabButton>
            <SectionTabButton
              active={tab === 'partners'}
              onClick={() => setTab('partners')}
            >
              Partner buttons
            </SectionTabButton>
          </div>
          <Label className="mt-3 block">Buttons</Label>

          {tab === 'agency' ? (
            <>
              {buttons.length === 0 ? (
                <div className="mt-1 rounded-md border border-dashed border-border bg-card/40 p-6 text-center font-sans text-[12px] text-muted-foreground">
                  No agency-controlled buttons yet.
                </div>
              ) : (
                <div className="mt-1 space-y-2">
                  {buttons.map((b, i) => (
                    <ProptreeButtonRow
                      key={b.id}
                      index={i}
                      button={b}
                      isDragging={draggingIndex === i}
                      formOptions={formOptions}
                      proptreeId={proptreeId}
                      onChange={(patch) => updateAt(i, patch)}
                      onRemove={() => removeAt(i)}
                      onDragStart={() => setDraggingIndex(i)}
                      onDragEnd={() => setDraggingIndex(null)}
                      onDrop={() => handleDrop(i)}
                    />
                  ))}
                </div>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => setModalOpen(true)}
                disabled={buttons.length >= 20}
              >
                <Plus />
                Create new button
              </Button>
              <AddProptreeButtonModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onAdd={(button) => onChange([...buttons, button])}
                formOptions={formOptions}
                proptreeId={proptreeId}
              />
              <p className="mt-2 font-mono text-[10px] leading-relaxed text-muted-foreground">
                Rendered after each agent's own buttons under a
                "Featured by the agency" divider.
              </p>
            </>
          ) : (
            <PartnerToggleList
              value={partners}
              onChange={onPartnersChange}
            />
          )}
        </div>
      </div>
    </div>
  );
}


function SectionTabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded px-3 py-1 font-sans text-[11px] font-semibold uppercase tracking-[0.06em] transition-colors',
        active
          ? 'bg-card text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

// === Marquee section (banner slogans + agency card) ===============
const SLOGAN_TAB_LABELS: Record<'top' | 'card', string> = {
  top: 'Banner · Slogans',
  card: 'Banner · Agency',
};

function MarqueeSection({
  value,
  onChange,
  showAgencyBanner,
  agencyBannerStats,
  onShowAgencyBannerChange,
  onAgencyBannerStatsChange,
  showAgentFeed,
  mainFeedHidden,
}: {
  value: BannerSlogans;
  onChange: (next: BannerSlogans) => void;
  showAgencyBanner: ProptreeEnumValue | null;
  agencyBannerStats: ProptreeEnumValue | null;
  onShowAgencyBannerChange: (next: ProptreeEnumValue | null) => void;
  onAgencyBannerStatsChange: (next: ProptreeEnumValue | null) => void;
  showAgentFeed: ProptreeEnumValue | null;
  mainFeedHidden: boolean;
}) {
  const [tab, setTab] = React.useState<'top' | 'card'>('top');
  const agencyBannerEnabled = showAgencyBanner?.value === 'enabled';

  return (
    <div className="p-5">
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <div className="inline-flex rounded-md border border-border bg-muted p-0.5 dark:border-white/10">
            {(Object.keys(SLOGAN_TAB_LABELS) as Array<'top' | 'card'>).map(
              (key) => (
                <SectionTabButton
                  key={key}
                  active={tab === key}
                  onClick={() => setTab(key)}
                >
                  {SLOGAN_TAB_LABELS[key]}
                </SectionTabButton>
              ),
            )}
          </div>
        </div>

        {tab === 'top' && (
          <>
            <div className="col-span-12 sm:col-span-6">
              <Label>Show top banner</Label>
              <SwitchBox
                checked={value.enabled}
                onChange={(v) => onChange({ ...value, enabled: v })}
                ariaLabel="Enable top section banner"
              />
            </div>
            {value.enabled && (
              <div className="col-span-12">
                <BannerSlogansEditor
                  value={value}
                  onChange={onChange}
                  showAgentFeed={showAgentFeed}
                  mainFeedHidden={mainFeedHidden}
                />
              </div>
            )}
          </>
        )}

        {tab === 'card' && (
          <>
            <div className="col-span-12 sm:col-span-6">
              <Label>Show agency banner</Label>
              <SwitchBox
                checked={agencyBannerEnabled}
                onChange={(v) =>
                  onShowAgencyBannerChange(
                    AGENCY_BANNER_OPTIONS.find(
                      (o) => o.value === (v ? 'enabled' : 'disabled'),
                    ) ?? null,
                  )
                }
                ariaLabel="Show agency banner"
              />
            </div>
            {agencyBannerEnabled && (
              <div className="col-span-12 sm:col-span-6">
                <Label>Banner stats</Label>
                <Select
                  className="mt-1"
                  options={BANNER_STATS_OPTIONS.map<SelectOption>((o) => ({
                    value: String(o.value),
                    label: o.name ?? '',
                  }))}
                  value={String(agencyBannerStats?.value ?? 'disabled')}
                  onChange={(v) =>
                    onAgencyBannerStatsChange(
                      BANNER_STATS_OPTIONS.find((o) => o.value === v) ?? null,
                    )
                  }
                />
              </div>
            )}
            <div className="col-span-12">
              <p className="font-sans text-[12px] leading-relaxed text-muted-foreground">
                <span className="mr-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-foreground/70">
                  OBS
                </span>
                Banner will show after the first “Load more” click, and
                only if more than 8 listings exist.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// === Slogan editor (presets + customs) ============================
const SLOGAN_PRESET_LABELS: Record<keyof BannerSlogans['presets'], string> = {
  activeListings: 'Active listings count',
  agents: 'Number of agents',
  listingsForSale: 'Listings for sale',
  listingsForRent: 'Listings for rent',
};
const SLOGAN_PRESET_PLACEHOLDERS: Record<
  keyof BannerSlogans['presets'],
  string
> = {
  activeListings: '“N active listings”',
  agents: '“N agents”',
  listingsForSale: '“N listings for sale”',
  listingsForRent: '“N listings for rent”',
};

function BannerSlogansEditor({
  value,
  onChange,
  showAgentFeed,
  mainFeedHidden,
}: {
  value: BannerSlogans;
  onChange: (next: BannerSlogans) => void;
  showAgentFeed: ProptreeEnumValue | null;
  mainFeedHidden: boolean;
}) {
  const presetKeys = Object.keys(value.presets) as Array<
    keyof BannerSlogans['presets']
  >;
  const agentsHidden = showAgentFeed?.value === 'disabled';

  const reasonFor = (
    key: keyof BannerSlogans['presets'],
  ): string | null => {
    if (key === 'activeListings' && mainFeedHidden) {
      return 'n/a — Listings are hidden';
    }
    if (key === 'agents' && agentsHidden) {
      return 'n/a — Show agents is disabled';
    }
    if (key === 'listingsForSale' && mainFeedHidden) {
      return 'n/a — Listings are hidden';
    }
    if (key === 'listingsForRent' && mainFeedHidden) {
      return 'n/a — Listings are hidden';
    }
    return null;
  };

  const setPreset = (key: keyof BannerSlogans['presets'], v: boolean) => {
    if (reasonFor(key) !== null) return;
    onChange({ ...value, presets: { ...value.presets, [key]: v } });
  };

  const [addOpen, setAddOpen] = React.useState(false);
  const addCustom = (text: string) => {
    if (value.customs.length >= MAX_CUSTOM_SLOGANS) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    onChange({ ...value, customs: [...value.customs, trimmed] });
  };
  const updateCustom = (i: number, text: string) => {
    const clipped = text.slice(0, SLOGAN_CHAR_LIMIT);
    onChange({
      ...value,
      customs: value.customs.map((s, j) => (j === i ? clipped : s)),
    });
  };
  const removeCustom = (i: number) =>
    onChange({
      ...value,
      customs: value.customs.filter((_, j) => j !== i),
    });

  const customCount = value.customs.length;
  const remainingCustomSlots = MAX_CUSTOM_SLOGANS - customCount;

  return (
    <div className="mt-3 space-y-4">
      <div className="rounded-md border border-border bg-card">
        <div className="border-b border-[hsl(var(--hairline))] px-4 py-2">
          <div className="mono-label text-muted-foreground">
            Predefined slogans
          </div>
        </div>
        <div className="divide-y divide-[hsl(var(--hairline))]">
          {presetKeys.map((key) => {
            const reason = reasonFor(key);
            const unavailable = reason !== null;
            return (
              <div
                key={key}
                className={cn(
                  'flex items-center justify-between gap-3 px-4 py-2.5',
                  unavailable && 'opacity-50',
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-sans text-[13px] text-foreground">
                    {SLOGAN_PRESET_LABELS[key]}
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground">
                    {SLOGAN_PRESET_PLACEHOLDERS[key]}
                    <span aria-hidden className="px-1.5 opacity-60">
                      ·
                    </span>
                    {reason ?? 'only shows if more than one exist'}
                  </div>
                </div>
                <SwitchToggle
                  checked={!unavailable && value.presets[key]}
                  onChange={(v) => setPreset(key, v)}
                  ariaLabel={SLOGAN_PRESET_LABELS[key]}
                  disabled={unavailable}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-md border border-border bg-card">
        <div className="flex items-center justify-between gap-3 border-b border-[hsl(var(--hairline))] px-4 py-2">
          <div className="mono-label text-muted-foreground">Custom slogans</div>
          <div className="font-mono text-[10px] text-muted-foreground">
            {customCount} / {MAX_CUSTOM_SLOGANS}
          </div>
        </div>
        {value.customs.length > 0 && (
          <div className="divide-y divide-[hsl(var(--hairline))]">
            {value.customs.map((text, i) => (
              <CustomSloganRow
                key={i}
                value={text}
                onChange={(v) => updateCustom(i, v)}
                onRemove={() => removeCustom(i)}
              />
            ))}
          </div>
        )}
        <div
          className={cn(
            'px-4 py-2',
            value.customs.length > 0 &&
              'border-t border-[hsl(var(--hairline))]',
          )}
        >
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAddOpen(true)}
            disabled={remainingCustomSlots <= 0}
          >
            <Plus />
            Add slogan
          </Button>
        </div>
      </div>

      <AddSloganModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={addCustom}
      />
    </div>
  );
}

function CustomSloganRow({
  value,
  onChange,
  onRemove,
}: {
  value: string;
  onChange: (v: string) => void;
  onRemove: () => void;
}) {
  const remaining = SLOGAN_CHAR_LIMIT - value.length;
  return (
    <div className="flex items-center gap-2 px-4 py-2">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={SLOGAN_CHAR_LIMIT}
        placeholder="e.g. Find your dream home"
        className="h-9 flex-1"
      />
      <span
        className={cn(
          'shrink-0 font-mono text-[10px] tabular-nums',
          remaining <= 10 ? 'text-amber-600' : 'text-muted-foreground',
        )}
      >
        {value.length}/{SLOGAN_CHAR_LIMIT}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        aria-label="Remove slogan"
        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
      >
        <Trash2 />
      </Button>
    </div>
  );
}

function AddSloganModal({
  open,
  onClose,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  onAdd: (text: string) => void;
}) {
  const [text, setText] = React.useState('');
  const [shown, setShown] = React.useState(open);
  const [visible, setVisible] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (open) {
      setShown(true);
      setText('');
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', onKey);
      const r1 = requestAnimationFrame(() => {
        const r2 = requestAnimationFrame(() => {
          setVisible(true);
          inputRef.current?.focus();
        });
        return () => cancelAnimationFrame(r2);
      });
      return () => {
        cancelAnimationFrame(r1);
        document.body.style.overflow = prev;
        window.removeEventListener('keydown', onKey);
      };
    }
    setVisible(false);
    const t = setTimeout(() => setShown(false), 200);
    return () => clearTimeout(t);
  }, [open, onClose]);

  if (!shown) return null;
  const trimmed = text.trim();
  const remaining = SLOGAN_CHAR_LIMIT - text.length;
  const submit = () => {
    if (!trimmed) return;
    onAdd(trimmed);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-black/60 transition-opacity duration-200 ease-out',
          visible ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        className={cn(
          'relative w-full max-w-md overflow-hidden rounded-2xl border border-[hsl(var(--hairline))] bg-card shadow-pop transition-all duration-200 ease-out',
          visible
            ? 'translate-y-0 scale-100 opacity-100'
            : 'translate-y-2 scale-95 opacity-0',
        )}
      >
        <div className="border-b border-[hsl(var(--hairline))] px-5 py-4">
          <h2 className="font-display text-[20px] font-medium leading-none tracking-[-0.01em]">
            Add slogan
          </h2>
          <p className="mt-1.5 font-sans text-[12px] text-muted-foreground">
            Short tagline to rotate in the marquee. Up to {SLOGAN_CHAR_LIMIT}{' '}
            characters.
          </p>
        </div>
        <div className="px-5 py-5">
          <Label htmlFor="add-slogan-input">Slogan</Label>
          <Input
            id="add-slogan-input"
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, SLOGAN_CHAR_LIMIT))}
            maxLength={SLOGAN_CHAR_LIMIT}
            placeholder="e.g. Find your dream home"
            className="mt-1"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
          />
          <div className="mt-1.5 text-right">
            <span
              className={cn(
                'font-mono text-[10px] tabular-nums',
                remaining <= 10 ? 'text-amber-600' : 'text-muted-foreground',
              )}
            >
              {text.length}/{SLOGAN_CHAR_LIMIT}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-[hsl(var(--hairline))] bg-muted/30 px-5 py-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={submit} disabled={!trimmed}>
            Add slogan
          </Button>
        </div>
      </div>
    </div>
  );
}

// === Switches =====================================================
function SwitchBox({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <div className="mt-1 flex h-11 items-center justify-between rounded-md border border-border bg-card px-3">
      <span className="font-sans text-[13px] text-foreground">
        {checked ? 'Enabled' : 'Disabled'}
      </span>
      <SwitchToggle
        checked={checked}
        onChange={onChange}
        ariaLabel={ariaLabel}
      />
    </div>
  );
}

function SwitchToggle({
  checked,
  onChange,
  ariaLabel,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => {
        if (disabled) return;
        onChange(!checked);
      }}
      disabled={disabled}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
        checked ? 'bg-primary' : 'bg-muted-foreground/30',
        disabled && 'cursor-not-allowed',
      )}
    >
      <span
        className={cn(
          'inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform',
          checked ? 'translate-x-[18px]' : 'translate-x-[2px]',
        )}
      />
    </button>
  );
}

// === Preview =====================================================
type Viewport = 'mobile' | 'desktop';
const VIEWPORT_WIDTH: Record<Viewport, number> = {
  mobile: 390,
  desktop: 1280,
};

interface ProptreePreviewPatch {
  // Agency-controlled buttons sent as the public `agencyControlledButtons`
  // override on the agent page. Disabled entries are stripped here so
  // the preview matches what a saved/published version would show.
  agencyControlledButtons?: Array<{
    id: string;
    title: string;
    subtitle: string | null;
    url: string;
    icon: string | null;
    image: string | null;
    images: string[] | null;
    type: string;
  }>;
  agencyBanner?: { visible?: boolean; showStats?: boolean };
  bannerSlogans?: BannerSlogans;
  showAgents?: boolean;
  // 'enabled' or 'disabled' — broadens / narrows the agent's listings
  // feed without a save round-trip. The renderer reads it as a query
  // param on every /listings refetch so changes apply instantly.
  showAllAgencyListings?: string | null;
  newsletter?: boolean;
  newsletterTitle?: string | null;
  newsletterSubtitle?: string | null;
  partners?: Record<string, boolean>;
}

function buildPreviewPatch(form: FormState): ProptreePreviewPatch {
  return {
    agencyControlledButtons: form.agencyButtons
      .filter((b) => b.enabled)
      .map((b) => ({
        id: b.id,
        title: b.title,
        subtitle: b.subtitle,
        url: b.url,
        icon: b.icon,
        image: b.image,
        images: b.images,
        type: b.type,
      })),
    agencyBanner: {
      visible: form.showAgencyBanner?.value === 'enabled',
      showStats: form.agencyBannerStats?.value === 'enabled',
    },
    bannerSlogans: {
      enabled: form.bannerSlogans.enabled,
      presets: { ...form.bannerSlogans.presets },
      customs: form.bannerSlogans.customs
        .map((s) => s.trim())
        .filter((s) => s.length > 0),
    },
    showAgents: form.showAgentFeed?.value === 'enabled',
    showAllAgencyListings:
      form.showAllAgencyListings?.value === 'enabled' ? 'enabled' : 'disabled',
    newsletter: form.newsletter,
    // Blank → null so the preview shows the default copy, same as a save.
    newsletterTitle: form.newsletterTitle.trim() || null,
    newsletterSubtitle: form.newsletterSubtitle.trim() || null,
    partners: form.partners,
  };
}

function ProptreePreview({
  form,
  agents,
  pickedId,
  onPick,
}: {
  form: FormState;
  agents: AgentsProptreePickerItem[];
  pickedId: string | null;
  onPick: (id: string) => void;
}) {
  const picked =
    agents.find((a) => a.id === pickedId) ?? agents[0] ?? null;
  const slug = picked?.alias ?? picked?.shortId ?? '';
  // Listings filter is delivered live via postMessage in the patch —
  // the iframe URL stays stable across toggles so we don't have to
  // remount it every time the user flips the dropdown.
  const baseUrl = slug ? withDemoFlag(`${PUBLIC_URL}/${slug}?preview=1`) : null;
  const [viewport, setViewport] = React.useState<Viewport>('desktop');
  const patch = React.useMemo(() => buildPreviewPatch(form), [form]);

  return (
    <Card className="flex h-full min-h-[640px] flex-col overflow-hidden p-0 lg:min-h-0">
      {/* Layout: "Preview" label pinned far-left (absolute), the
          viewport toggle naturally centered via flex justify-center,
          the agent picker pinned far-right (absolute). Mirrors the
          agency-page preview header so both pages share the rhythm. */}
      <div className="relative flex shrink-0 items-center justify-center gap-3 border-b border-[hsl(var(--hairline))] px-5 py-3">
        <span className="mono-label absolute left-5 text-muted-foreground">
          Preview
        </span>
        <ViewportToggle value={viewport} onChange={setViewport} />
        <div className="absolute right-5">
          <AgentPicker
            agents={agents}
            pickedId={pickedId}
            onPick={onPick}
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden bg-muted/40">
        {baseUrl ? (
          <ScaledFrame
            key={slug}
            url={baseUrl}
            viewport={viewport}
            patch={patch}
          />
        ) : (
          <div className="flex h-full items-center justify-center px-6 text-center font-mono text-[11px] text-muted-foreground">
            No agent proptrees yet — add an agent to enable the preview.
          </div>
        )}
      </div>
      {picked && (
        <div className="border-t border-[hsl(var(--hairline))] bg-muted/30 px-5 py-2 text-[11px] text-muted-foreground">
          Previewing{' '}
          <a
            href={`${PUBLIC_URL}/${slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-foreground underline-offset-2 hover:underline"
          >
            {picked.name ?? picked.alias ?? picked.shortId ?? 'agent'}
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}
    </Card>
  );
}

function AgentPicker({
  agents,
  pickedId,
  onPick,
}: {
  agents: AgentsProptreePickerItem[];
  pickedId: string | null;
  onPick: (id: string) => void;
}) {
  const picked = agents.find((a) => a.id === pickedId) ?? agents[0] ?? null;
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const ref = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open]);

  // Reset the filter every time the dropdown opens, and focus the
  // search field so the user can start typing immediately.
  React.useEffect(() => {
    if (open) {
      setQuery('');
      // The DOM node only exists once the dropdown is mounted, so
      // schedule the focus a tick after open flips.
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter((a) => {
      const haystacks = [a.name, a.alias, a.shortId];
      return haystacks.some(
        (h) => typeof h === 'string' && h.toLowerCase().includes(q),
      );
    });
  }, [agents, query]);

  if (agents.length === 0) return null;
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-8 max-w-[220px] items-center gap-2 rounded-md border border-border bg-card px-2 pr-2 text-[12px] hover:bg-muted"
      >
        <PickerAvatar item={picked} />
        <span className="min-w-0 truncate">
          {picked?.name ?? picked?.alias ?? picked?.shortId ?? 'Pick agent'}
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 flex w-[280px] flex-col rounded-md border border-border bg-card shadow-pop">
          {/* Search input. Filters by name / alias / shortId, fuzzy-
              matching on substring. Sticky at the top of the popover
              so a long agency list doesn't push it off-screen as the
              user scrolls. */}
          <div className="border-b border-[hsl(var(--hairline))] p-2">
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search agents…"
              className="h-8 text-[12px]"
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  setOpen(false);
                }
              }}
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-center text-[12px] text-muted-foreground">
                No agents match “{query}”.
              </div>
            ) : (
              filtered.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => {
                    onPick(a.id);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] hover:bg-muted',
                    a.id === pickedId && 'bg-muted/50',
                  )}
                >
                  <PickerAvatar item={a} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-foreground">
                      {a.name ?? a.alias ?? a.shortId ?? '(unnamed)'}
                    </div>
                    {a.alias && (
                      <div className="truncate font-mono text-[10px] text-muted-foreground">
                        @{a.alias}
                      </div>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PickerAvatar({ item }: { item: AgentsProptreePickerItem | null }) {
  if (!item) return null;
  const label = item.name ?? item.alias ?? item.shortId ?? '?';
  if (item.image) {
    return (
      <img
        src={item.image}
        alt={label}
        className="h-6 w-6 shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted font-mono text-[9px] font-bold uppercase">
      {initials(label)}
    </div>
  );
}

function ViewportToggle({
  value,
  onChange,
}: {
  value: Viewport;
  onChange: (v: Viewport) => void;
}) {
  return (
    <div className="inline-flex shrink-0 rounded-md border border-border bg-muted p-0.5 dark:border-white/10">
      {(['mobile', 'desktop'] as const).map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={cn(
            'rounded px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.06em] transition-colors',
            value === v
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {v}
        </button>
      ))}
    </div>
  );
}

function ScaledFrame({
  url,
  viewport,
  patch,
}: {
  url: string;
  viewport: Viewport;
  patch: ProptreePreviewPatch;
}) {
  if (viewport === 'mobile') {
    return <MobileFrame url={url} patch={patch} />;
  }
  return <DesktopScaledFrame url={url} patch={patch} />;
}

function MobileFrame({
  url,
  patch,
}: {
  url: string;
  patch: ProptreePreviewPatch;
}) {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  usePreviewBridge(iframeRef, patch);
  return (
    <iframe
      ref={iframeRef}
      src={url}
      title="Proptree preview"
      className="h-full w-full border-0 bg-background"
    />
  );
}

function DesktopScaledFrame({
  url,
  patch,
}: {
  url: string;
  patch: ProptreePreviewPatch;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [size, setSize] = React.useState({ scale: 1, nativeH: 800 });
  const nativeW = VIEWPORT_WIDTH.desktop;
  usePreviewBridge(iframeRef, patch);

  React.useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (!w || !h) return;
      const scale = Math.min(w / nativeW, 1);
      const nativeH = scale > 0 ? h / scale : h;
      setSize({ scale, nativeH });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [nativeW]);

  return (
    <div
      ref={containerRef}
      className="flex h-full w-full items-start justify-center overflow-hidden"
    >
      <div
        style={{
          width: nativeW * size.scale,
          height: size.nativeH * size.scale,
        }}
      >
        <iframe
          ref={iframeRef}
          src={url}
          title="Proptree preview"
          style={{
            width: nativeW,
            height: size.nativeH,
            transform: `scale(${size.scale})`,
            transformOrigin: 'top left',
          }}
          className="border-0 bg-background"
        />
      </div>
    </div>
  );
}

function usePreviewBridge(
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  patch: ProptreePreviewPatch,
) {
  const patchRef = React.useRef(patch);
  React.useEffect(() => {
    patchRef.current = patch;
  }, [patch]);

  React.useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'proptr:preview', patch },
      '*',
    );
  }, [iframeRef, patch]);

  React.useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const d = e.data;
      if (d && typeof d === 'object' && d.type === 'proptr:preview-ready') {
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'proptr:preview', patch: patchRef.current },
          '*',
        );
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [iframeRef]);
}
